import {
  ViolationTypeCreateSchema,
  isBehaviorViolationTypeKey,
  type ViolationTypeCreateRequest,
  type ViolationTypeUpdateRequest,
} from '@aysar/contracts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { settingsGroupPath } from '@/lib/route.constant';
import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { Callout } from '@/shared/components/feedback/Callout/Callout';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { PermissionGate } from '@/shared/components/permissions/PermissionGate/PermissionGate';
import { Button } from '@/shared/components/ui/Button/Button';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import { buildViolationTypeFields } from '../../config/violation-type-form.config';
import { useTemplateKeyOptions } from '../../hooks/useTemplateKeyOptions/useTemplateKeyOptions';
import {
  useCreateViolationTypeMutation,
  useUpdateViolationTypeMutation,
  useViolationTypeQuery,
} from '../../services/violations/violations.api';
import { violationErrorCopy } from '../../utils/violation-error/violation-error.util';

/** The settings group the behavioural limits live in — see `moderation.behavior.*`. */
const BEHAVIOR_SETTINGS_GROUP = 'moderation';

/**
 * Create or edit a violation rule.
 *
 * Validated against `ViolationTypeCreateSchema` in **both** modes, because the
 * update contract is the create contract minus the immutable `key` — and the
 * form still renders that, disabled, so an edit never looks like a different
 * form.
 *
 * The PATCH body is assembled by hand rather than spread, and the reason is
 * `suspensionDays`: an edit that switches a rule from `suspend` to `ban` must
 * send `null` explicitly, or the stored length would survive a consequence that
 * has none. The api normalises the same way (`needsSuspensionDays(action) ?
 * days : null`), so the two agree — belt and braces on the one field where a
 * leftover value is a live suspension nobody chose.
 *
 * A **behavioural** rule (P27a3) omits two more keys from that body: the review
 * switch is locked on and the guidance is never read, so neither is a thing the
 * form can change — and the api would refuse the one and ignore the other. The
 * form says why in a notice above the fields, with the way to the limits that
 * actually govern the rule.
 */
export function ViolationTypeFormModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('violations');
  const toast = useToast();

  const typeId = typeof modalData?.['typeId'] === 'string' ? modalData['typeId'] : null;
  const isEditing = typeId !== null;

  const detail = useViolationTypeQuery(typeId ?? '', { skip: typeId === null });
  const [createType] = useCreateViolationTypeMutation();
  const [updateType] = useUpdateViolationTypeMutation();

  // Only an EXISTING key can be behavioural — the three are seeded and a create
  // with one of them is refused as taken.
  const isBehavioral = isEditing && isBehaviorViolationTypeKey(detail.data?.key ?? '');

  /**
   * The form's own key and notice key, mirrored so the picker can offer the
   * rule's per-type notice while the operator is still typing the rule's key.
   * A create form has no record to read them from, and the option list is not
   * part of the value layer — it is what the value layer chooses FROM.
   */
  const [draft, setDraft] = useState<{ key: string; templateKey: string }>({
    key: '',
    templateKey: '',
  });

  const onValuesChange = useCallback((values: Record<string, unknown>) => {
    setDraft({
      key: typeof values['key'] === 'string' ? values['key'] : '',
      templateKey:
        typeof values['legalNoticeTemplateKey'] === 'string'
          ? values['legalNoticeTemplateKey']
          : '',
    });
  }, []);

  const templateKeyOptions = useTemplateKeyOptions({
    typeKey: draft.key === '' ? (detail.data?.key ?? '') : draft.key,
    current:
      draft.templateKey === '' ? (detail.data?.legalNoticeTemplateKey ?? '') : draft.templateKey,
  });

  return (
    <DynamicFormModal<ViolationTypeCreateRequest>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={
        isEditing
          ? t('types.form.editTitle', { name: detail.data?.name ?? '' })
          : t('types.form.createTitle')
      }
      size="lg"
      // Held until the record arrives: RHF reads `defaultValues` once, so a form
      // mounted early opens blank and saving would wipe the rule.
      isLoading={isEditing && detail.isLoading}
      fields={buildViolationTypeFields(t, { isEditing, isBehavioral, templateKeyOptions })}
      onValuesChange={onValuesChange}
      schema={ViolationTypeCreateSchema}
      banner={
        isBehavioral ? (
          <Callout tone="info" title={t('types.form.behaviorTitle')}>
            <span className="flex flex-col items-start gap-sm">
              <span>{t('types.form.behaviorNotice')}</span>
              <PermissionGate permission="settings.read">
                <Button asChild variant="secondary" size="sm">
                  <Link to={settingsGroupPath(BEHAVIOR_SETTINGS_GROUP)}>
                    {t('types.form.behaviorSettingsLink')}
                  </Link>
                </Button>
              </PermissionGate>
            </span>
          </Callout>
        ) : undefined
      }
      initialValues={
        isEditing
          ? {
              key: detail.data?.key,
              severity: detail.data?.severity,
              action: detail.data?.action,
              suspensionDays: detail.data?.suspensionDays,
              reviewBeforeEnforce: detail.data?.reviewBeforeEnforce,
              legalNoticeTemplateKey: detail.data?.legalNoticeTemplateKey,
              classifierGuidance: detail.data?.classifierGuidance,
              isActive: detail.data?.isActive,
              translations: detail.data?.translations,
            }
          : // A new rule defaults to the two safest answers: it is in use (or it
            // would be created inert), and it waits for a human before doing
            // anything to anybody.
            { severity: 'medium', action: 'warn', reviewBeforeEnforce: true, isActive: true }
      }
      onCancel={onCancel}
      onSubmit={async (values) => {
        try {
          if (isEditing) {
            // `key` is absent from the update contract by construction.
            const body: ViolationTypeUpdateRequest = {
              severity: values.severity,
              action: values.action,
              suspensionDays: values.action === 'suspend' ? values.suspensionDays : null,
              legalNoticeTemplateKey: values.legalNoticeTemplateKey,
              isActive: values.isActive,
              translations: values.translations,
            };
            if (!isBehavioral) {
              body.reviewBeforeEnforce = values.reviewBeforeEnforce;
              body.classifierGuidance = values.classifierGuidance;
            }
            await updateType({ id: typeId, body }).unwrap();
            toast.success(t('types.form.updated'));
          } else {
            await createType(values).unwrap();
            toast.success(t('types.form.created'));
          }
          onCancel();
        } catch (error) {
          const apiError = toApiError(error);
          // A refusal this module knows is said in the operator's language; the
          // rest fall through to the api's own sentence.
          const copy = violationErrorCopy(apiError);
          throw copy === null ? apiError : { ...apiError, message: t(copy.i18nKey) };
        }
      }}
    />
  );
}
